/*
  KIH cart toast
  --------------
  cart-drawer.js still re-renders the drawer markup after every add so the
  drawer is current whenever the shopper opens it from the header - it just no
  longer forces itself open. This listens for the `kih:cart-added` event that
  replaces that auto-open and shows a small confirmation instead.

  Deliberately has no checkout button: checkout lives on /cart.
*/
(() => {
  const AUTO_DISMISS_MS = 6000;
  const THUMB_WIDTH = 160; // 56px slot at 2x, with headroom

  class KihCartToast extends HTMLElement {
    connectedCallback() {
      this.mediaEl = this.querySelector('[data-toast-media]');
      this.titleEl = this.querySelector('[data-toast-title]');
      this.closeButton = this.querySelector('[data-toast-close]');
      this.timer = null;

      this.closeButton?.addEventListener('click', () => this.hide());
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.hide();
      });

      // Pausing the countdown on hover/focus keeps the toast from vanishing
      // while someone is reaching for "View cart".
      ['mouseenter', 'focusin'].forEach((evt) => this.addEventListener(evt, () => this.clearTimer()));
      ['mouseleave', 'focusout'].forEach((evt) => this.addEventListener(evt, () => this.startTimer()));

      document.addEventListener('kih:cart-added', (event) => this.show(event.detail));

      // If the shopper opens the drawer from the header the toast is redundant.
      document.querySelector('cart-drawer')?.addEventListener('kih:drawer-open', () => this.hide());
    }

    show(detail = {}) {
      if (this.titleEl) {
        this.titleEl.textContent = detail.title || this.dataset.fallbackTitle || '';
      }

      if (this.mediaEl) {
        if (detail.image) {
          this.mediaEl.src = this.thumbnail(detail.image);
          this.mediaEl.alt = '';
          this.mediaEl.hidden = false;
        } else {
          this.mediaEl.hidden = true;
          this.mediaEl.removeAttribute('src');
        }
      }

      this.dataset.open = 'true';
      this.startTimer();
    }

    // /cart/add.js hands back the unresized original, which can be a 2000px
    // JPEG for a 56px slot. Ask the CDN for a sized variant instead.
    thumbnail(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', String(THUMB_WIDTH));
        return parsed.toString();
      } catch (error) {
        return url;
      }
    }

    hide() {
      this.clearTimer();
      this.dataset.open = 'false';
    }

    startTimer() {
      this.clearTimer();
      this.timer = setTimeout(() => this.hide(), AUTO_DISMISS_MS);
    }

    clearTimer() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    }
  }

  if (!customElements.get('kih-cart-toast')) {
    customElements.define('kih-cart-toast', KihCartToast);
  }
})();
